import { useEffect, useRef, useState } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import type { Session } from '@supabase/supabase-js';
import { trpc } from './trpc';
import { supabase } from '@/integrations/supabase/client';

// A cached token with less than this left is treated as expired, to cover
// request transit time (the backend checks `exp` with no grace).
const TOKEN_MIN_REMAINING_MS = 10_000;

/**
 * tRPC Provider
 *
 * Wraps the app with tRPC client.
 * Handles authentication by injecting Supabase session token into all requests.
 *
 * Note: This should be wrapped inside QueryClientProvider from App.tsx
 */
export function TRPCProvider({
  children,
  queryClient
}: {
  children: React.ReactNode;
  queryClient: QueryClient;
}) {
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      sessionRef.current = session;
    });

    return () => subscription.unsubscribe();
  }, []);

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          // Backend URL from environment
          url: `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:59321'}/trpc`,

          // Add authentication and organization headers
          async headers() {
            // Send the cached token while it has life left; once it is expired
            // or nearly, ask Supabase, which refreshes first (one shared
            // refresh). Not cache-only: Supabase pauses its refresh timer in
            // hidden tabs, so after an hour away the cached token had expired
            // and every focus refetch 401'd until a reload. Not getSession()
            // every time: it takes Supabase's cross-tab lock and near expiry
            // waits on a refresh to supabase.co, which China reaches directly,
            // not via the HK proxy. Only the auth listener writes sessionRef.
            let session = sessionRef.current;
            const expiresAtMs = session?.expires_at ? session.expires_at * 1000 : 0;
            if (expiresAtMs - Date.now() < TOKEN_MIN_REMAINING_MS) {
              try {
                ({
                  data: { session },
                } = await supabase.auth.getSession());
              } catch {
                session = null; // Keep request unauthenticated if session cannot be read.
              }
            }
            const sessionToken = session?.access_token ?? '';

            // Active org resolution: only use explicit client-side org selection.
            // If absent, backend falls back to first active membership.
            const orgId = localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || '';

            const headers: Record<string, string> = {};
            if (sessionToken) headers.authorization = `Bearer ${sessionToken}`;
            if (orgId) headers['x-org-id'] = orgId;
            return headers;
          },

          // Optional: Add batch size limit
          maxURLLength: 2083,
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      {children}
    </trpc.Provider>
  );
}
